import { Controller } from "@hotwired/stimulus"

/**
 * Controller for the playground registration modal.
 * Handles setting the redirect cookie before showing the modal.
 *
 * Usage:
 * - Attach to a <dialog> element
 * - Trigger via window event: window.dispatchEvent(new CustomEvent('show-registration-modal'))
 * - Or via data-action on elements within the controller scope
 */
export default class extends Controller {
  static values = {
    redirectPath: String,
    setRedirectUrl: String,
    csrfToken: String,
  }

  declare redirectPathValue: string
  declare setRedirectUrlValue: string
  declare csrfTokenValue: string

  private boundShowHandler = this.show.bind(this)

  connect() {
    window.addEventListener("show-registration-modal", this.boundShowHandler as EventListener)
  }

  disconnect() {
    window.removeEventListener("show-registration-modal", this.boundShowHandler as EventListener)
  }

  /**
   * Show the modal and set the redirect cookie.
   * Called when user clicks the signup prompt or via window event.
   * Playground state (prompt, models, params) is preserved via localStorage.
   */
  async show(event?: Event) {
    event?.preventDefault()

    // Set the redirect cookie on the server (path only, no prompt data)
    if (this.setRedirectUrlValue && this.redirectPathValue) {
      try {
        await fetch(this.setRedirectUrlValue, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": this.csrfToken,
          },
          body: JSON.stringify({ redirect_path: this.redirectPathValue }),
        })
      } catch (e) {
        console.warn("Failed to set redirect cookie:", e)
      }
    }

    const dialog = this.element as HTMLDialogElement
    dialog.showModal()
  }

  close() {
    const dialog = this.element as HTMLDialogElement
    dialog.close()
  }

  // Prefer the per-session token rendered in the (uncached) frame; the page
  // <meta> token is stale on the CDN-cached model page where the mini lives.
  private get csrfToken(): string {
    if (this.csrfTokenValue) return this.csrfTokenValue
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta?.getAttribute("content") || ""
  }
}
